import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { deckService, cardService, cardProgressService, storageService } from "@/services/firestore";
import { Deck, Flashcard, FlashcardProgress, CardSide } from "@/types";
import { Button } from "@/components/ui/button";
import JSZip from "jszip";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
} from "@/components/ui/card";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Plus, 
  ArrowLeft, 
  Play, 
  Settings, 
  Trash2, 
  Edit2, 
  Search,
  Globe,
  Lock,
  Download,
  Calendar,
  Layers,
  Sparkles,
  Trophy,
  Activity,
  ArrowRight,
  BookOpen,
  CircleHelp,
  Music,
  FileArchive,
  UploadCloud
} from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Papa from "papaparse";
import { Helmet } from "react-helmet-async";
import { importService } from "@/services/importService";

export default function DeckDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuthStore();
  const [deck, setDeck] = useState<Deck | null>(null);
  const [cards, setCards] = useState<Flashcard[]>([]);
  const [progress, setProgress] = useState<FlashcardProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddCardOpen, setIsAddCardOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // New card form state
  const [frontText, setFrontText] = useState("");
  const [backText, setBackText] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [frontAudio, setFrontAudio] = useState<File | null>(null);
  const [backAudio, setBackAudio] = useState<File | null>(null);
  const [tags, setTags] = useState("");
  const [frontLang, setFrontLang] = useState("en-US");
  const [backLang, setBackLang] = useState("vi-VN");
  const [submitting, setSubmitting] = useState(false);
  
  // CSV/ZIP Import state
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isZipOpen, setIsZipOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [isCloning, setIsCloning] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();
  const isOwner = user && deck && deck.userId === user.uid;

  useEffect(() => {
    if (user && id) {
      const fetchData = async () => {
        try {
          const [deckData, cardsData, progressData] = await Promise.all([
            deckService.getDeck(id),
            cardService.getCardsByDeck(id),
            cardProgressService.getAllProgressForDeck(user.uid, id)
          ]);

          if (deckData) {
            setDeck(deckData);
            setCards(cardsData);
            setProgress(progressData);
          } else {
            navigate("/decks");
          }
        } catch (error) {
          console.error("Error fetching deck details:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [user, id]);

  const filteredCards = useMemo(() => {
    return cards.filter(card => 
      card.front.text.toLowerCase().includes(searchQuery.toLowerCase()) || 
      card.back.text.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [cards, searchQuery]);

  const stats = useMemo(() => {
    const now = new Date();
    const mastered = progress.filter(p => p.repetition >= 5).length;
    const learning = progress.filter(p => p.repetition > 0 && p.repetition < 5).length;
    const dueCount = progress.filter(p => {
       const nextDate = p.nextReview?.toDate ? p.nextReview.toDate() : new Date(p.nextReview);
       return nextDate <= now;
    }).length;
    const newCount = cards.length - progress.length;

    return { 
      mastered, 
      learning, 
      due: dueCount + (newCount > 0 ? newCount : 0),
      new: newCount,
      totalProgress: cards.length > 0 ? Math.round((mastered / cards.length) * 100) : 0
    };
  }, [cards, progress]);

  const handleCreateCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !id) return;
    setSubmitting(true);
    try {
      const frontSide: CardSide = {
        text: frontText,
        lang: frontLang
      };
      const backSide: CardSide = {
        text: backText,
        lang: backLang
      };

      if (frontAudio) {
        const path = `decks/${id}/cards/${Date.now()}_front_${frontAudio.name}`;
        frontSide.audio = await storageService.uploadFile(frontAudio, path);
      }

      if (backAudio) {
        const path = `decks/${id}/cards/${Date.now()}_back_${backAudio.name}`;
        backSide.audio = await storageService.uploadFile(backAudio, path);
      }

      await cardService.createCard(id, {
        front: frontSide,
        back: backSide,
        tags: tags.split(",").map(t => t.trim()).filter(t => t !== ""),
      });
      
      setFrontText(""); setBackText(""); setFrontAudio(null); setBackAudio(null); setTags(""); setIsAddCardOpen(false);
      const updatedCards = await cardService.getCardsByDeck(id);
      setCards(updatedCards);
    } catch (error) {
      console.error("Error creating card:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleZipImport = async () => {
    if (!zipFile) return;
    setImporting(true);
    setImportProgress(0);
    setImportStatus("Đang khởi tạo...");
    try {
      const result = await importService.importDeckFromZip(zipFile, (current: number, total: number, status: string) => {
        setImportProgress(current);
        setImportStatus(status);
      });
      alert(`Nhập thành công ${result.cardCount} thẻ!`);
      setIsZipOpen(false);
      setZipFile(null);
      // Refresh current deck state if needed or redirect
      window.location.reload(); 
    } catch (error: any) {
      alert(error.message || "Lỗi khi nhập ZIP");
    } finally {
      setImporting(false);
      setImportProgress(0);
      setImportStatus("");
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (confirm("Bạn có chắc chắn muốn xóa thẻ này?")) {
      try {
        await cardService.deleteCard(cardId);
        setCards(prev => prev.filter(c => c.id !== cardId));
      } catch (error) {
        console.error("Error deleting card:", error);
      }
    }
  };

  const handleDeleteDeck = async () => {
    if (!id || !deck) return;
    
    if (confirm(`Bạn có chắc chắn muốn xóa bộ thẻ "${deck.title}"? Thao tác này sẽ xóa tất cả thẻ, tiến độ học tập và hình ảnh/âm thanh và không thể hoàn tác.`)) {
      setIsDeleting(true);
      try {
        await deckService.deleteDeck(id);
        navigate("/decks");
      } catch (error) {
        console.error("Error deleting deck:", error);
        alert("Có lỗi xảy ra khi xóa bộ thẻ. Vui lòng thử lại.");
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleTogglePublic = async () => {
    if (!deck || !id) return;
    setIsPublishing(true);
    try {
      const newStatus = !deck.isPublic;
      const updateData: any = { isPublic: newStatus };
      if (newStatus && user?.displayName) updateData.createdBy = user.displayName;
      await deckService.updateDeck(id, updateData);
      setDeck(prev => prev ? { ...prev, ...updateData } : null);
    } catch (error) {
      console.error("Error toggling public status:", error);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile || !id) return;
    setImporting(true);
    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const newCards: Flashcard[] = [];
        try {
          for (const row of results.data as any[]) {
            const front = row.front || row.Front || row.question || row.Term;
            const back = row.back || row.Back || row.answer || row.Definition;
            const frontLang = row.front_lang || row.frontLang || row.FrontLang;
            const backLang = row.back_lang || row.backLang || row.BackLang;
            
            if (front && back) {
              const cardData = { 
                front: { text: String(front), lang: frontLang ? String(frontLang) : "en-US" },
                back: { text: String(back), lang: backLang ? String(backLang) : "vi-VN" },
                tags: row.tags ? String(row.tags).split(";").map(t => t.trim()) : [],
                box: 1,
                nextReview: new Date()
              };
              const docRef = await cardService.createCard(id, cardData);
              newCards.push({ id: docRef.id, deckId: id, ...cardData, createdAt: new Date() });
            }
          }
          setCards(prev => [...prev, ...newCards]);
          setIsImportOpen(false); setCsvFile(null);
          alert(`Đã nhập thành công ${newCards.length} thẻ!`);
        } catch (error) {
          console.error("Error importing CSV:", error);
        } finally {
          setImporting(false);
        }
      }
    });
  };

  const handleCloneDeck = async () => {
    if (!user || !id) { navigate("/login"); return; }
    setIsCloning(true);
    try {
      const newId = await deckService.cloneDeck(user.uid, id);
      navigate(`/decks/${newId}`);
    } catch (error) {
      console.error("Error cloning deck:", error);
    } finally {
      setIsCloning(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!deck) return null;

  return (
    <div className="max-w-6xl mx-auto py-8 md:py-12 px-4 md:px-6 space-y-12">
      <Helmet>
        <title>{deck.title} | Flashcard Platform</title>
      </Helmet>

      {/* Header and Breadcrumbs */}
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-sm font-bold text-muted-foreground uppercase tracking-widest">
          <Link to="/decks" className="hover:text-primary transition-colors flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Thư viện
          </Link>
          <span>/</span>
          <span className="text-foreground truncate max-w-[200px]">{deck.title}</span>
        </div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-2">
            <h1 className="text-5xl font-extrabold tracking-tight leading-none text-zinc-900 italic uppercase italic">{deck.title}</h1>
            <p className="text-xl text-muted-foreground font-medium max-w-2xl leading-relaxed italic">
              {deck.description || "BềEsưu tập các thẻ học giúp bạn ghi nhềEhiệu quả hơn."}
            </p>
          </div>
          <div className="flex gap-4">
            <Link 
              to={`/decks/${id}/study`}
              className={cn(buttonVariants({ size: "lg", className: "h-14 px-8 rounded-2xl shadow-lg shadow-primary/20 text-lg font-bold" }), cards.length === 0 && "pointer-events-none opacity-50")}
            >
              <Play className="mr-2 h-5 w-5 fill-current" /> Học ngay {stats.due > 0 && `(${stats.due})`}
            </Link>
            {isOwner && (
              <>
                <Button 
                variant="outline" 
                size="lg" 
                className="h-14 px-6 rounded-2xl border-2 font-bold gap-2"
                onClick={() => setIsAddCardOpen(true)}
              >
                <Plus className="h-5 w-5" /> Thêm thẻ
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="h-14 px-6 rounded-2xl border-2 font-bold gap-2 text-destructive hover:bg-destructive/5 hover:text-destructive border-destructive/20"
                onClick={handleDeleteDeck}
                disabled={isDeleting}
              >
                <Trash2 className="h-5 w-5" /> {isDeleting ? "Đang xóa..." : "Xóa bộ thẻ"}
              </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid (2-column layout with sidebar) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* Main Content Column */}
        <div className="lg:col-span-8 space-y-12">
          
          {/* Progress Section */}
          <div className="space-y-8">
            <div className="space-y-1 border-b-2 border-zinc-100 pb-6">
               <h2 className="text-2xl font-black italic uppercase tracking-tighter text-zinc-900 leading-none">Tiến đềEhọc tập</h2>
               <p className="text-muted-foreground font-medium text-sm italic">Phân tích hành trình chinh phục kiến thức của bạn.</p>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Thành thạo", value: stats.mastered.toString(), icon: Trophy, color: "text-amber-500", bg: "bg-amber-500/10" },
                { label: "Cần luyện thêm", value: stats.learning.toString(), icon: Activity, color: "text-emerald-500", bg: "bg-emerald-500/10" },
                { label: "Cần ôn ngay", value: stats.due.toString(), icon: Calendar, color: "text-indigo-500", bg: "bg-indigo-500/10" },
                { label: "Thẻ mới", value: stats.new.toString(), icon: Sparkles, color: "text-primary", bg: "bg-primary/10" },
              ].map((stat, i) => (
                <Card key={i} className="border-2 rounded-2xl p-6 text-center group hover:border-primary/30 transition-all">
                  <div className={cn("p-2 rounded-xl mx-auto mb-3 w-fit", stat.bg, stat.color)}>
                    <stat.icon className="h-4 w-4" />
                  </div>
                  <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">{stat.label}</p>
                  <p className="text-2xl font-black mt-1">{stat.value}</p>
                </Card>
              ))}
            </div>

            {progress.length > 0 ? (
               <div className="py-12 border-2 border-dashed rounded-[3rem] bg-zinc-50/50 flex flex-col items-center justify-center px-10 text-center">
                  <div className="flex items-center gap-4 mb-4">
                    <Activity className="h-6 w-6 text-primary animate-pulse" />
                    <span className="text-2xl font-bold italic uppercase">Bạn đã học được {stats.totalProgress}% bềEthẻ</span>
                  </div>
                  <p className="text-muted-foreground font-medium italic">Tiến đềEđược lưu lại tự động sau mỗi lần bạn xếp hạng (Again/Good/...) cho từng thẻ đơn lẻ.</p>
               </div>
            ) : (
              <div className="py-24 text-center rounded-[3rem] bg-zinc-50 border-2 border-dashed border-zinc-200">
                <Activity className="h-20 w-20 text-zinc-200 mx-auto mb-6" />
                <h3 className="text-xl font-bold text-zinc-400 uppercase italic tracking-widest">Chưa có dữ liệu học tập</h3>
                <p className="text-zinc-500 font-bold max-w-xs mx-auto mt-2 italic">Hãy hoàn thành ít nhất 1 phiên học đềEbắt đầu theo dõi tiến đềE</p>
              </div>
            )}
          </div>

          {/* Card List Section */}
          <div className="space-y-8 pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b-2 border-zinc-100 pb-6 gap-6">
              <div className="space-y-1">
                <h2 className="text-2xl font-black italic uppercase tracking-tighter text-zinc-900 leading-none">Danh sách thẻ ({cards.length})</h2>
                <p className="text-muted-foreground font-medium text-sm italic">Tất cả kiến thức trong bềEthẻ này.</p>
              </div>
              <div className="relative group max-w-sm flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Tìm nội dung thẻ..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-11 w-full rounded-xl bg-muted/30 border-none pl-10 focus:ring-primary/20 font-bold" 
                />
              </div>
            </div>

            {filteredCards.length > 0 ? (
              <ul className="space-y-4">
                {filteredCards.map((card) => {
                   const cardProgress = progress.find(p => p.cardId === card.id);
                   const isNew = !cardProgress;
                   const isMastered = cardProgress && cardProgress.repetition >= 5;

                   return (
                    <li key={card.id} className="group flex items-center justify-between p-4 md:p-6 bg-white border border-zinc-100/80 rounded-3xl hover:border-primary/20 hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.05)] transition-all">
                        <div className="flex items-center gap-4 md:gap-6 flex-1 min-w-0">
                          {card.front.image && (
                            <div className="h-14 w-14 rounded-lg overflow-hidden border-2 border-zinc-100 flex-shrink-0">
                              <img src={card.front.image} alt="" className="h-full w-full object-cover" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-zinc-950 truncate">{card.front.text}</p>
                              {isNew && <span className="px-2 py-0.5 rounded-md bg-zinc-100 text-[8px] font-black text-zinc-400 uppercase tracking-widest">New</span>}
                              {isMastered && <Trophy className="h-3 w-3 text-amber-500" />}
                            </div>
                            <p className="text-sm text-zinc-500 truncate mt-0.5">{card.back.text}</p>
                          </div>
                        </div>
                        {isOwner && (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                             <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-background shadow-sm">
                              <Edit2 className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-destructive/10 text-destructive shadow-sm" onClick={() => card.id && handleDeleteCard(card.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                    </li>
                   );
                })}
              </ul>
            ) : (
              <div className="py-24 text-center border-2 border-dashed rounded-[3rem] bg-muted/20">
                <div className="bg-background w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm border">
                  <BookOpen className="h-10 w-10 text-muted-foreground/30" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Chưa có thẻ nào</h3>
                <p className="text-muted-foreground mb-8 max-w-xs mx-auto text-lg leading-relaxed">Hãy bắt đầu bằng cách thêm thẻ học đầu tiên.</p>
                <Button onClick={() => setIsAddCardOpen(true)} className="rounded-xl h-14 px-10 font-bold shadow-lg shadow-primary/20">Thêm thẻ mới</Button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar Space */}
        <div className="lg:col-span-4 space-y-10">
          {/* Metadata Card */}
          <Card className="rounded-[2.5rem] border-2 shadow-sm overflow-hidden sticky top-8">
            <CardHeader className="bg-muted/30 border-b p-8">
              <CardTitle className="text-xl font-black uppercase italic tracking-tighter tracking-widest">Thông tin bềEthẻ</CardTitle>
            </CardHeader>
            <CardContent className="p-8 space-y-8">
              <div className="flex items-center gap-5">
                <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary font-black text-2xl uppercase shadow-inner">
                  {deck.createdBy?.[0] || user?.displayName?.[0] || "U"}
                </div>
                <div>
                  <p className="font-black text-xl leading-none mb-1.5">{deck.createdBy || user?.displayName || "Knowledge Master"}</p>
                  <p className="text-[10px] text-muted-foreground font-black uppercase tracking-[0.2em]">Curator & Author</p>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  { label: "Trạng thái", value: deck.isPublic ? "Công khai" : "Riêng tư", icon: Globe, status: deck.isPublic ? "success" : "neutral" },
                  { label: "Ngày khởi tạo", value: deck.createdAt?.toDate ? new Date(deck.createdAt.toDate()).toLocaleDateString('vi-VN') : new Date(deck.createdAt).toLocaleDateString('vi-VN'), icon: Calendar },
                  { label: "HềEthống", value: "SM-2 SRS", icon: CircleHelp, status: "success" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-zinc-50/50 group transition-all duration-300">
                    <div className="flex items-center gap-3">
                      <item.icon className="h-4 w-4 text-zinc-300 group-hover:text-primary transition-colors" />
                      <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">{item.label}</span>
                    </div>
                    <span className={cn(
                      "font-black text-[10px] tracking-widest uppercase px-3 py-1 rounded-lg border-2 transition-all",
                      item.status === 'success' ? "bg-emerald-50 text-emerald-600 border-emerald-100" : "bg-white text-zinc-800 border-zinc-100"
                    )}>{item.value}</span>
                  </div>
                ))}
              </div>

              {isOwner && (
                <div className="space-y-3 pt-4 border-t border-zinc-100">
                  <Button 
                    variant="outline" 
                    className="w-full h-14 rounded-2xl border-2 font-black uppercase tracking-widest text-[10px] gap-3"
                    onClick={handleTogglePublic}
                    disabled={isPublishing}
                  >
                    {deck.isPublic ? <Lock className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                    {deck.isPublic ? "Chuyển sang Riêng tư" : "Công khai bềEthẻ"}
                  </Button>
                  
                  <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
                    <DialogTrigger render={<Button variant="outline" className="w-full h-14 rounded-2xl border-2 font-black uppercase tracking-widest text-[10px] gap-3 bg-zinc-50">
                        <Layers className="h-4 w-4" /> Batch Import (CSV)
                      </Button>} />
                    <DialogContent className="sm:max-w-md rounded-[2.5rem] border-2 shadow-2xl p-8">
                      <DialogHeader className="mb-4">
                        <DialogTitle className="text-2xl font-black italic uppercase">Batch Acquisition</DialogTitle>
                        <DialogDescription className="text-muted-foreground font-medium">Tải lên file CSV với các cột 'front' và 'back'.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-6 py-4">
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground pl-1">Chọn file</Label>
                          <Input type="file" accept=".csv" className="h-14 rounded-xl border-2 cursor-pointer bg-zinc-50" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button className="w-full h-14 rounded-xl font-black uppercase tracking-widest text-sm italic" onClick={handleCsvImport} disabled={!csvFile || importing}>
                          {importing ? "Executing..." : "Confirm Import"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={isZipOpen} onOpenChange={setIsZipOpen}>
                    <DialogTrigger render={<Button variant="outline" className="w-full h-14 rounded-2xl border-2 font-black uppercase tracking-widest text-[10px] gap-3 bg-zinc-50">
                        <FileArchive className="h-4 w-4" /> Import Audio (ZIP)
                      </Button>} />
                    <DialogContent className="sm:max-w-md rounded-[2.5rem] border-2 shadow-2xl p-8">
                      <DialogHeader className="mb-4">
                        <DialogTitle className="text-2xl font-black italic uppercase">Media Synchronization</DialogTitle>
                        <DialogDescription className="text-muted-foreground font-medium">Tải lên file ZIP chứa các file *.mp3. Tên file nên khớp với nội dung thẻ.</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-6 py-4">
                        <div className="space-y-2">
                          <Label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground pl-1">Chọn tệp ZIP</Label>
                          <Input 
                            type="file" 
                            accept=".zip" 
                            className="h-14 rounded-xl border-2 cursor-pointer bg-zinc-50" 
                            onChange={(e) => setZipFile(e.target.files?.[0] || null)} 
                            disabled={importing}
                          />
                        </div>

                        {importing && (
                          <div className="space-y-3 pt-2">
                            <div className="flex justify-between items-end px-1">
                              <span className="text-[10px] font-black uppercase tracking-widest text-violet-600 animate-pulse">
                                {importStatus}
                              </span>
                              <span className="text-[10px] font-black text-zinc-400">
                                {importProgress}%
                              </span>
                            </div>
                            <Progress value={importProgress} className="h-2 rounded-full" />
                          </div>
                        )}

                        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
                          <p className="text-[11px] font-medium text-amber-900 leading-relaxed flex gap-2">
                            <span className="text-amber-500 font-bold">INFO:</span>
                            Cấu trúc yêu cầu: Một file metadata.json ềEgốc thư mục kèm theo folder assets chứa hình ảnh/âm thanh.
                          </p>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button 
                          className="w-full h-14 rounded-xl font-black uppercase tracking-widest text-sm italic" 
                          onClick={handleZipImport} 
                          disabled={!zipFile || importing}
                        >
                          {importing ? "Processing Sync..." : "Synchronize Now"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Link to={`/decks/${id}/edit`} className={cn(buttonVariants({ variant: "outline", size: "lg", className: "w-full h-14 rounded-2xl border-2 font-black uppercase tracking-widest text-[10px] gap-3" }))}>
                    <Settings className="h-4 w-4" /> Tùy chỉnh bềEthẻ
                  </Link>
                </div>
              )}

              {!isOwner && (
                <Button variant="outline" size="lg" className="w-full h-14 rounded-2xl border-2 font-black uppercase tracking-widest text-[10px] gap-3" onClick={handleCloneDeck} disabled={isCloning}>
                  <Download className="h-4 w-4" /> Sao chép đềEhọc tập
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Card Dialog */}
      <Dialog open={isAddCardOpen} onOpenChange={setIsAddCardOpen}>
        <DialogContent className="sm:max-w-xl rounded-[3rem] border-none shadow-[0_32px_120px_-20px_rgba(0,0,0,0.3)] bg-white p-10">
          <DialogHeader className="space-y-4 mb-8 text-center">
            <DialogTitle className="text-4xl font-black tracking-tighter italic uppercase leading-none">New Knowledge</DialogTitle>
            <DialogDescription className="text-lg font-bold text-zinc-400 italic">Thêm một viên gạch mới cho nền tảng kiến thức của bạn.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCard} className="space-y-8">
            <div className="space-y-6">
              <div className="space-y-3">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 pl-1">Mặt trước (Key Concept)</Label>
                <Textarea 
                  placeholder="Nhập thuật ngữ..." 
                  className="min-h-[100px] rounded-2xl border-2 bg-zinc-50/50 p-6 text-xl font-bold focus:bg-white transition-all resize-none shadow-sm"
                  value={frontText} onChange={(e) => setFrontText(e.target.value)} required 
                />
              </div>
              <div className="space-y-3">
                <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 pl-1">Mặt sau (Core Knowledge)</Label>
                <Textarea 
                  placeholder="Nhập định nghĩa..." 
                  className="min-h-[100px] rounded-2xl border-2 bg-zinc-50/50 p-6 text-lg font-medium focus:bg-white transition-all resize-none italic shadow-sm"
                  value={backText} onChange={(e) => setBackText(e.target.value)} required 
                />
              </div>

              {/* Language Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 pl-1">Ngôn ngữ mặt trước</Label>
                  <select 
                    value={frontLang}
                    onChange={(e) => setFrontLang(e.target.value)}
                    className="w-full h-12 rounded-xl border-2 bg-zinc-50/50 px-4 text-sm font-bold focus:bg-white transition-all outline-none"
                  >
                    <option value="en-US">English (US)</option>
                    <option value="vi-VN">Tiếng Việt</option>
                    <option value="ja-JP">Japanese</option>
                    <option value="ko-KR">Korean</option>
                    <option value="fr-FR">French</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 pl-1">Ngôn ngữ mặt sau</Label>
                  <select 
                    value={backLang}
                    onChange={(e) => setBackLang(e.target.value)}
                    className="w-full h-12 rounded-xl border-2 bg-zinc-50/50 px-4 text-sm font-bold focus:bg-white transition-all outline-none"
                  >
                    <option value="vi-VN">Tiếng Việt</option>
                    <option value="en-US">English (US)</option>
                    <option value="ja-JP">Japanese</option>
                    <option value="ko-KR">Korean</option>
                    <option value="fr-FR">French</option>
                  </select>
                </div>
              </div>

              {/* Audio Uploads */}
              <div className="grid grid-cols-2 gap-4 p-6 bg-zinc-50 rounded-2xl border-2 border-dashed border-zinc-200">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Music className="w-3 h-3 text-indigo-500" />
                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Audio Mặt Trước</Label>
                  </div>
                  <Input 
                    type="file" 
                    accept="audio/*" 
                    onChange={(e) => setFrontAudio(e.target.files?.[0] || null)}
                    className="h-10 text-[10px] bg-white border-zinc-200 cursor-pointer"
                  />
                  {frontAudio && <p className="text-[9px] text-emerald-600 font-bold truncate">✁E{frontAudio.name}</p>}
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Music className="w-3 h-3 text-indigo-500" />
                    <Label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Audio Mặt Sau</Label>
                  </div>
                  <Input 
                    type="file" 
                    accept="audio/*" 
                    onChange={(e) => setBackAudio(e.target.files?.[0] || null)}
                    className="h-10 text-[10px] bg-white border-zinc-200 cursor-pointer"
                  />
                  {backAudio && <p className="text-[9px] text-emerald-600 font-bold truncate">✁E{backAudio.name}</p>}
                </div>
              </div>
            </div>
            
            <DialogFooter className="flex-col sm:flex-col gap-4">
              <Button type="submit" className="w-full h-16 rounded-2xl text-xl font-black tracking-tighter uppercase italic shadow-2xl shadow-primary/20" disabled={submitting}>
                {submitting ? "Executing..." : "Lưu thẻ ngay"}
              </Button>
              <Button type="button" variant="ghost" className="w-full font-black uppercase tracking-widest text-[10px] text-zinc-400 hover:text-zinc-950" onClick={() => setIsAddCardOpen(false)}>Hủy bỏ</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
